// Reading FALCON PLM v2.0.41 backup files (used by the import screen; tested on its own).

export interface V2Source { name: string; data: any }

/**
 * Accepts either backup type v2.0.41 makes:
 * - an organisation backup ({ falconOrganizationBackup: true, organization, organizationData })
 * - a full platform backup / saved state ({ falconPlmState: true, organizations, organizationData: { uid: data } })
 * - an older single-company file (the data object itself)
 */
export function readV2Backup(json: unknown): V2Source[] {
  const v: any = json;
  if (!v || typeof v !== "object") throw new Error("That file isn't a FALCON PLM backup.");
  if (v.falconOrganizationBackup === true && v.organizationData && typeof v.organizationData === "object") {
    return [{ name: String(v.organization?.name || "Organisation"), data: v.organizationData }];
  }
  const state = v.falconPlmState === true ? v : v.appState?.falconPlmState === true ? v.appState : null;
  if (state && state.organizationData && typeof state.organizationData === "object") {
    const orgs: any[] = Array.isArray(state.organizations) ? state.organizations : [];
    return Object.entries(state.organizationData)
      .filter(([, d]) => d && typeof d === "object")
      .map(([uid, d]) => ({ name: String(orgs.find((o) => o?.uid === uid)?.name || uid), data: d }));
  }
  if (Array.isArray(v.products) || Array.isArray(v.fabrics) || v.database) return [{ name: "Organisation", data: v }];
  throw new Error("That file isn't a FALCON PLM backup. In v2.0.41 use Settings → Backup & Restore → Download Backup.");
}

export interface V2Counts { lists: number; suppliers: number; agents: number; fabrics: number; garments: number; files: number; pos: number; tasks: number; samples: number; techpacks: number; templates: number; library: number; rd: number; qc: number; compliance: number; shipments: number; packingLists: number; landed: number; later: Record<string, number> }

const list = (v: any) => (Array.isArray(v) ? v : []);

/** What this slice imports now, and what later slices will bring across. */
export function countV2(data: any): V2Counts {
  const d = data?.database || {};
  const lists = ["brand", "speciality", "category", "standard", "colourName", "productFeature", "cat", "atpv", "cvc", "structure"].reduce((n, k) => n + list(d[k]).length, 0);
  const files = list(data?.products).reduce((n: number, p: any) => n + (p?.productImage?.dataUrl ? 1 : 0) + (p?.datasheet?.dataUrl ? 1 : 0), 0)
    + ["brandAssets", "standardAssets", "washCareLibrary"].reduce((n, k) => n + list(data?.[k]).filter((x: any) => (x?.logo || x?.icon)?.dataUrl).length, 0)
    + list(d.colourName).filter((c: any) => c?.image?.dataUrl).length;
  return {
    lists, files,
    suppliers: list(data?.suppliers).length,
    agents: list(data?.agents).length,
    fabrics: list(data?.fabrics).length,
    garments: list(data?.products).length,
    pos: list(data?.purchaseOrders).length,
    tasks: list(data?.tasks).length,
    samples: list(data?.sampleRecords).length,
    techpacks: list(data?.techpacks).length,
    templates: list(data?.techpackMeasurementTemplates).length,
    library: list(data?.techpackLibrary).length,
    rd: list(data?.rdProjects).length,
    qc: qcRecords(data).length,
    compliance: list(data?.complianceRecords).length,
    shipments: shipmentRecords(data).length,
    packingLists: shipmentRecords(data).reduce((n, x) => n + x.packingLists.length, 0),
    landed: landedRecords(data).length,
    later: {},
  };
}

export interface ImportStep { section: string; label: string; payload: Record<string, unknown>; bytes: number }

const size = (x: unknown) => JSON.stringify(x).length;
function stripFiles(p: any) {
  const { productImage, datasheet, ...rest } = p || {};
  void productImage; void datasheet;
  return rest;
}

/** Break the import into requests small enough to send one at a time (under ~3.5 MB each). */
export function planV2Import(data: any, maxBytes = 3_500_000): ImportStep[] {
  const steps: ImportStep[] = [];
  const assets = (k: string) => list(data?.[k]).map((x: any) => ({ name: x?.name, primaryColor: x?.primaryColor, secondaryColor: x?.secondaryColor, footerText: x?.footerText }));
  // Colour images travel separately (below); the list step only needs the names and codes.
  const database = { ...(data?.database || {}), colourName: list(data?.database?.colourName).map((c: any) => (c && typeof c === "object" ? { ...c, image: null } : c)) };
  steps.push({ section: "lists", label: "Setup lists", payload: { database, brandAssets: assets("brandAssets"), standardAssets: assets("standardAssets"), washCareLibrary: assets("washCareLibrary"), datasheetSettings: data?.datasheetSettings || null }, bytes: 0 });
  const batch = (section: string, label: string, records: any[], per = 200) => {
    for (let i = 0; i < records.length; i += per) steps.push({ section, label, payload: { records: records.slice(i, i + per) }, bytes: 0 });
  };
  batch("suppliers", "Suppliers", list(data?.suppliers));
  batch("agents", "Agents", list(data?.agents));
  batch("fabrics", "Fabrics", list(data?.fabrics));
  batch("products", "Garments", list(data?.products).map(stripFiles), 100);
  for (const [k, listKey, field] of [["brandAssets", "brand", "logo"], ["standardAssets", "standard", "icon"], ["washCareLibrary", "washCare", "icon"]] as const) {
    for (const x of list(data?.[k])) {
      const f = x?.[field];
      if (f?.dataUrl && x?.name) steps.push({ section: "listIcon", label: "Logos and icons", payload: { listKey, value: x.name, file: { name: f.name, dataUrl: f.dataUrl } }, bytes: 0 });
    }
  }
  for (const c of list(data?.database?.colourName)) {
    const f = c?.image;
    if (f?.dataUrl && (c?.colour || c?.name)) steps.push({ section: "listIcon", label: "Logos and icons", payload: { listKey: "colourName", value: c.colour || c.name, file: { name: f.name, dataUrl: f.dataUrl } }, bytes: 0 });
  }
  for (const p of list(data?.products)) {
    for (const [kind, f] of [["image", p?.productImage], ["datasheet", p?.datasheet]] as const) {
      if (f?.dataUrl && p?.uid) steps.push({ section: "productFile", label: "Garment images and datasheets", payload: { legacyUid: p.uid, kind, file: { name: f.name, dataUrl: f.dataUrl } }, bytes: 0 });
    }
  }
  const containers = list(data?.logistics?.containerTypes);
  if (containers.length) steps.push({ section: "containers", label: "Container settings", payload: { containers }, bytes: 0 });
  // Stage 2.3: the Tech Pack library (as trims), measurement templates and Tech Packs come before purchase orders,
  // so PO lines can pin their Tech Pack revision; sample rounds and R&D come after.
  const noData = (f: any) => (f && typeof f === "object" ? { name: f.name, type: f.type } : f);
  batch("library", "Trims & packaging library", list(data?.techpackLibrary).map((x: any) => ({ ...x, image: noData(x?.image), artwork: noData(x?.artwork), revisionHistory: list(x?.revisionHistory).map((h: any) => ({ ...h, snapshot: h?.snapshot ? { ...h.snapshot, image: null, artwork: null } : null })) })), 100);
  for (const x of list(data?.techpackLibrary)) {
    for (const kind of ["image", "artwork"] as const) {
      const f = x?.[kind];
      if (f?.dataUrl && x?.uid) steps.push({ section: "devFile", label: "Library images and artwork", payload: { target: "library", kind, legacyUid: x.uid, file: { name: f.name, dataUrl: f.dataUrl } }, bytes: 0 });
    }
  }
  batch("templates", "Measurement templates", list(data?.techpackMeasurementTemplates), 100);
  const tpNoFiles = (t: any) => ({ ...t, attachments: [], revisionHistory: list(t?.revisionHistory).map((h: any) => ({ ...h, snapshot: h?.snapshot ? { ...h.snapshot, attachments: [], revisionHistory: [] } : null })) });
  batch("techpacks", "Tech Packs", list(data?.techpacks).map(tpNoFiles), 20);
  for (const t of list(data?.techpacks)) {
    for (const f of list(t?.attachments)) if (f?.dataUrl && t?.uid) steps.push({ section: "devFile", label: "Tech Pack attachments", payload: { target: "techpack", legacyUid: t.uid, file: { name: f.name, dataUrl: f.dataUrl } }, bytes: 0 });
  }
  batch("pos", "Purchase orders", list(data?.purchaseOrders).map(slimPo), 25);
  batch("samples", "Sample rounds", list(data?.sampleRecords).map((x: any) => ({ ...x, photos: [] })), 100);
  for (const x of list(data?.sampleRecords)) {
    for (const f of list(x?.photos)) if (f?.dataUrl && x?.uid) steps.push({ section: "devFile", label: "Sample photos", payload: { target: "sample", legacyUid: x.uid, caption: f.caption || "", file: { name: f.name || "photo.jpg", dataUrl: f.dataUrl } }, bytes: 0 });
  }
  const emailOf = (uid: unknown) => list(data?.operators).find((o: any) => o?.uid === uid)?.email || "";
  batch("tasks", "Tasks", list(data?.tasks).map((t: any) => ({ ...t, assignedToEmail: emailOf(t?.assignedToUid) })), 200);
  batch("rd", "R&D developments", list(data?.rdProjects).map((x: any) => ({ ...x, attachments: [], assignedToEmail: emailOf(x?.assignedToUid) })), 50);
  for (const x of list(data?.rdProjects)) {
    for (const f of list(x?.attachments)) if (f?.dataUrl && x?.uid) steps.push({ section: "devFile", label: "R&D files", payload: { target: "rd", legacyUid: x.uid, file: { name: f.name, dataUrl: f.dataUrl } }, bytes: 0 });
  }
  // QC / AQL reports (1st and 2nd QC) with their photos, and compliance records with their PDFs (Stage 2.4).
  const qc = qcRecords(data);
  batch("qc", "QC / AQL reports", qc.map((x) => ({ ...x, report: { ...x.report, photos: list(x.report?.photos).map((p: any) => ({ uid: p?.uid, name: p?.name, caption: p?.caption, uploadedAt: p?.uploadedAt, uploadedBy: p?.uploadedBy })) } })), 50);
  for (const x of qc) {
    for (const p of list(x.report?.photos)) if (p?.dataUrl && p?.uid) steps.push({ section: "devFile", label: "QC photos", payload: { target: "qc", legacyUid: x.uid, photoUid: p.uid, caption: p.caption || "", file: { name: p.name || "photo.jpg", dataUrl: p.dataUrl } }, bytes: 0 });
  }
  batch("compliance", "Compliance records", list(data?.complianceRecords).map((x: any) => ({ ...x, document: x?.document ? { name: x.document.name, uploadedAt: x.document.uploadedAt, uploadedBy: x.document.uploadedBy } : null })), 200);
  for (const x of list(data?.complianceRecords)) {
    if (x?.document?.dataUrl && x?.uid) steps.push({ section: "devFile", label: "Compliance PDFs", payload: { target: "compliance", legacyUid: x.uid, file: { name: x.document.name || "certificate.pdf", dataUrl: x.document.dataUrl } }, bytes: 0 });
  }
  // Stage 2.5: shipments (with their packing lists) after purchase orders, then their PDFs, then landed costs.
  const ships = shipmentRecords(data);
  batch("shipments", "Shipments and packing lists", ships.map((x) => ({ ...x, documents: x.documents.map(({ dataUrl, ...d }) => { void dataUrl; return d; }) })), 50);
  for (const x of ships) {
    for (const d of x.documents) if (d.dataUrl) steps.push({ section: "devFile", label: "Shipping documents", payload: { target: "shipment", legacyUid: x.uid, kind: d.kind, file: { name: d.name || "document.pdf", dataUrl: d.dataUrl } }, bytes: 0 });
  }
  batch("landed", "Landed costings", landedRecords(data), 100);
  return rebalance(steps, maxBytes);
}

const DOC_KINDS = ["commercialInvoice", "certificateOfOrigin", "customsDocument", "otherDocument"];
/** A v2.0.41 shipment line key ("LINE_<uid>", or the older "LINE_<n>_<product>_<COLOUR>") → the PO line's uid. */
function lineUidFor(po: any, key: string): string {
  const lines = list(po?.lines);
  if (lines.some((l: any) => `LINE_${String(l?.lineUid || "").trim()}` === key)) return key.slice(5);
  const hit = lines.find((l: any, i: number) => `LINE_${Number(l?.lineNumber || i + 1)}_${String(l?.productUid || "product").trim()}_${String(l?.colour || "").trim().toUpperCase()}` === key);
  return String(hit?.lineUid || "");
}
const qtyOf = (v: any) => ({ qtyBySize: v?.qtyBySize && typeof v.qtyBySize === "object" ? v.qtyBySize : {}, totalQty: Number(v?.totalQty || 0) });

/**
 * v2.0.41 shipments, one record per shipment: a PO's own shipments (po.shipping.shipmentBundles) and consolidated
 * shipments (db.consolidatedShipments, carried by several POs). Old bundles that only listed whole lines (itemKeys)
 * carry what was left of each line, as v2.0.41 itself did.
 */
export function shipmentRecords(data: any) {
  const out: Array<{ uid: string; consolidated: boolean; poUid: string; number: number; details: any; status: string; receivedAt: string; receivedBy: string;
    allocations: Array<{ poUid: string; lineUid: string; qtyBySize: Record<string, number>; totalQty: number; closed: any }>; documents: Array<{ kind: string; name: string; dataUrl?: string }>;
    packingLists: Array<{ poUid: string; list: any }> }> = [];
  const pos = list(data?.purchaseOrders);
  const detailsOf = (b: any) => ({
    method: b?.method || "", container: b?.container || "", bolNumber: b?.bolNumber || "", carrier: b?.method === "Air" ? b?.airline || b?.vessel || "" : b?.vessel || b?.airline || "",
    loadingPort: b?.loadingPort || "", dischargePort: b?.dischargePort || "", etd: b?.shipmentEtd || "", eta: b?.shipmentEta || "",
    originalEtd: b?.originalShipmentEtd || "", originalEta: b?.originalShipmentEta || "", currentStop: b?.currentStop || "",
  });
  const docsOf = (b: any) => [
    ...(b?.bolDocument?.dataUrl || b?.bolDocument?.name ? [{ kind: "bol", name: String(b.bolDocument.name || "bol.pdf"), dataUrl: b.bolDocument.dataUrl }] : []),
    ...DOC_KINDS.filter((k) => b?.shippingDocuments?.[k]?.dataUrl).map((k) => ({ kind: k, name: String(b.shippingDocuments[k].name || `${k}.pdf`), dataUrl: b.shippingDocuments[k].dataUrl })),
  ];
  // Packing rows point at lines by v2.0.41 key; send the line's uid with each row.
  const packOf = (b: any, po?: any) => (b?.packingList && typeof b.packingList === "object"
    ? { ...b.packingList, rows: list(b.packingList.rows).map((row: any) => ({ ...row, lineUid: lineUidFor(po, String(row?.lineKey || "")) })) } : null);
  const consolidated = new Map<string, any>();
  for (const c of list(data?.consolidatedShipments)) if (c?.uid) consolidated.set(String(c.uid), c);
  const seenConsolidated = new Map<string, (typeof out)[number]>();
  for (const po of pos) {
    const claimed = new Map<string, number>();
    const bundles = Object.values(po?.shipping?.shipmentBundles || {}).sort((a: any, b: any) => Number(a?.shipmentNumber || 0) - Number(b?.shipmentNumber || 0));
    for (const b of bundles as any[]) {
      if (!b || typeof b !== "object") continue;
      let allocs: Record<string, any> = b.allocations && typeof b.allocations === "object" && Object.keys(b.allocations).length ? b.allocations : {};
      if (!Object.keys(allocs).length) {
        allocs = {};
        for (const key of list(b.itemKeys)) {
          const uid = lineUidFor(po, key), line = list(po?.lines).find((l: any) => String(l?.lineUid) === uid);
          if (!line) continue;
          const left = Math.max(0, Number(line.totalQty || 0) - (claimed.get(key) || 0));
          if (left > 0) allocs[key] = { totalQty: left, qtyBySize: claimed.get(key) ? {} : line.qtyBySize || {} };
        }
      }
      for (const [k, v] of Object.entries(allocs)) claimed.set(k, (claimed.get(k) || 0) + Number((v as any)?.totalQty || 0));
      const lines = Object.entries(allocs).map(([key, v]) => ({ poUid: String(po.uid), lineUid: lineUidFor(po, key), ...qtyOf(v), closed: b.closedBalances?.[key] ? { ...qtyOf(b.closedBalances[key]), closedAt: b.closedBalances[key].closedAt || "", closedBy: b.closedBalances[key].closedBy || "", reason: b.closedBalances[key].reason || "Factory shortfall" } : null }))
        .filter((l) => l.lineUid && l.totalQty > 0);
      const pack = packOf(b, po);
      if (b.consolidated) {
        const uid = String(b.consolidatedUid || b.uid || "");
        if (!uid) continue;
        const rec = consolidated.get(uid) || b;
        let x = seenConsolidated.get(uid);
        if (!x) {
          x = { uid, consolidated: true, poUid: "", number: Number(rec.shipmentNumber || b.shipmentNumber || 1), details: detailsOf(rec), status: String(rec.status || b.status || "Loading"),
            receivedAt: String(rec.receivedAt || rec.receivedDate || ""), receivedBy: String(rec.receivedBy || ""), allocations: [], documents: docsOf(rec), packingLists: [] };
          seenConsolidated.set(uid, x);
          out.push(x);
        }
        const closedByPo = rec.poClosedBalances?.[po.uid] || {};
        x.allocations.push(...lines);
        for (const [key, v] of Object.entries(closedByPo)) {
          const hit = x.allocations.find((l) => l.poUid === String(po.uid) && l.lineUid === lineUidFor(po, key));
          if (hit && !hit.closed) hit.closed = { ...qtyOf(v), closedAt: (v as any)?.closedAt || "", closedBy: (v as any)?.closedBy || "", reason: (v as any)?.reason || "Factory shortfall" };
        }
        if (pack) x.packingLists.push({ poUid: String(po.uid), list: pack });
      } else {
        if (!lines.length) continue;
        out.push({ uid: `${po.uid}:${b.uid || b.shipmentNumber}`, consolidated: false, poUid: String(po.uid), number: Number(b.shipmentNumber || 1), details: detailsOf(b), status: String(b.status || "Loading"),
          receivedAt: String(b.receivedAt || b.receivedDate || ""), receivedBy: String(b.receivedBy || ""), allocations: lines, documents: docsOf(b), packingLists: pack ? [{ poUid: String(po.uid), list: pack }] : [] });
      }
    }
  }
  return out.filter((x) => x.allocations.length);
}

/** v2.0.41 landed costings (one per PO); a charge for one shipment points at that shipment's import id. */
export function landedRecords(data: any) {
  return list(data?.purchaseOrders).filter((po: any) => po?.landedCosting && typeof po.landedCosting === "object").map((po: any) => {
    const r = po.landedCosting;
    const shipUid = (bundleUid: string) => {
      if (!bundleUid) return "";
      const b = po?.shipping?.shipmentBundles?.[bundleUid];
      return b?.consolidated ? String(b.consolidatedUid || b.uid) : `${po.uid}:${bundleUid}`;
    };
    return {
      poUid: String(po.uid), status: r.status === "Final" ? "final" : "draft", method: r.allocationMethod === "quantity" ? "quantity" : "value", notes: String(r.notes || ""),
      charges: list(r.charges).map((c: any) => ({ id: c?.uid, category: c?.category, description: c?.description || "", shipmentUid: shipUid(String(c?.bundleUid || "")), currency: c?.currency || "USD", amount: Number(c?.amount || 0), fxToUsd: Number(c?.fxToUsd || 1) })),
      createdBy: r.createdBy || "", updatedBy: r.updatedBy || "", finalizedAt: r.finalizedAt || "", finalizedBy: r.finalizedBy || "",
      goodsValueSnapshot: r.goodsValueSnapshot ?? null, poRevisionSnapshot: r.poRevisionSnapshot === "" ? null : r.poRevisionSnapshot ?? null,
    };
  });
}

/** The AQL reports kept on each v2.0.41 PO line (1st and 2nd QC) with the Time & Action Plan review of each. */
export function qcRecords(data: any): Array<{ uid: string; lineUid: string; poNumber: string; round: 1 | 2; report: any; review: any }> {
  const out: Array<{ uid: string; lineUid: string; poNumber: string; round: 1 | 2; report: any; review: any }> = [];
  for (const po of list(data?.purchaseOrders)) {
    for (const [lineUid, q] of Object.entries(po?.qcByLine || {})) {
      const x: any = q || {};
      for (const [round, key] of [[1, "first"], [2, "second"]] as const) {
        const report = x.aqlReports?.[key];
        if (!report || typeof report !== "object") continue;
        const pre = round === 2 ? "review2" : "review";
        out.push({
          uid: String(report.uid || `${po?.uid}:${lineUid}:${key}`), lineUid, poNumber: String(po?.poNumber || ""), round, report,
          review: { status: x[`${pre}Status`] || "", photoNotes: x[`${pre}PhotoNotes`] || {}, overall: x[`${pre}OverallNotes`] || "", date: x[`${pre}SubmittedDate`] || "", at: x[`${pre}SubmittedAt`] || "", by: x[`${pre}SubmittedBy`] || "" },
        });
      }
    }
  }
  return out;
}

/** Only what Stage 2.2 needs from a v2.0.41 purchase order (photos and documents come later). */
function slimPo(po: any) {
  const qc: Record<string, any> = {};
  for (const [k, q] of Object.entries(po?.qcByLine || {})) {
    const x: any = q || {};
    qc[k] = { reviewStatus: x.reviewStatus || "", reviewSubmittedDate: x.reviewSubmittedDate || "", reviewSubmittedBy: x.reviewSubmittedBy || "", review2Status: x.review2Status || "", review2SubmittedDate: x.review2SubmittedDate || "", review2SubmittedBy: x.review2SubmittedBy || "" };
  }
  const bundles = Object.values(po?.shipping?.shipmentBundles || {}).map((b: any) => ({ status: b?.status || "", receivedAt: b?.receivedAt || b?.receivedDate || "", receivedBy: b?.receivedBy || "", allocations: Object.fromEntries(Object.entries(b?.allocations || {}).map(([k, v]: any) => [k, { totalQty: Number(v?.totalQty || 0) }])) }));
  const strip = (l: any) => { const { techPackLinkSnapshot, fabricSnapshot, ...rest } = l || {}; void techPackLinkSnapshot; void fabricSnapshot; return rest; };
  return {
    uid: po?.uid, poNumber: po?.poNumber, agentUid: po?.agentUid, agentName: po?.agentName, supplierUid: po?.supplierUid, supplierName: po?.supplierName, supplierSnapshot: po?.supplierSnapshot || null,
    orderDate: po?.orderDate, etc: po?.etc, etd: po?.etd, eta: po?.eta, criticalEtd: po?.criticalEtd, criticalEta: po?.criticalEta, raisedBy: po?.raisedBy, notes: po?.notes,
    status: po?.orderStatus || po?.status || "", cancelled: po?.cancelled === true, containerPlan: po?.containerPlan || null, revision: po?.revision || 0,
    lastRevisionAt: po?.lastRevisionAt || "", lastRevisionBy: po?.lastRevisionBy || "", lastRevisionSummary: po?.lastRevisionSummary || [],
    revisionHistory: list(po?.revisionHistory).map((r: any) => ({ revision: r?.revision, archivedAt: r?.archivedAt, archivedBy: r?.archivedBy, changeSummary: r?.changeSummary || [], snapshot: r?.snapshot ? { ...r.snapshot, lines: list(r.snapshot.lines).map(strip) } : null })),
    lines: list(po?.lines).map(strip),
    criticalPath: Object.fromEntries(Object.entries(po?.criticalPath || {}).map(([k, v]: any) => { const { qcPhotos, qc2Photos, ...rest } = v || {}; void qcPhotos; void qc2Photos; return [k, rest]; })), qcByLine: qc, receivedByGroup: po?.shipping?.receivedByGroup || {}, bundles,
    createdAt: po?.createdAt || "",
  };
}

/** Split any batch that is still too large in half, until every request fits. */
function rebalance(steps: ImportStep[], maxBytes: number): ImportStep[] {
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < steps.length; i++) {
      const st = steps[i];
      st.bytes = size(st.payload);
      const recs: any[] = (st.payload.records as any[]) || [];
      if (st.bytes > maxBytes && recs.length > 1) {
        const half = Math.ceil(recs.length / 2);
        steps.splice(i, 1, { ...st, payload: { records: recs.slice(0, half) }, bytes: 0 }, { ...st, payload: { records: recs.slice(half) }, bytes: 0 });
        changed = true;
        break;
      }
    }
  }
  for (const st of steps) st.bytes = size(st.payload);
  return steps;
}
